import { mobileAccess } from "#skyward_global";
import { createModalPositioningManager, ModalLayer, newModalLayer } from "#skyward_modalLayer";
import { registerLayoutResizeCallback } from "#skyward_window";
import { JQueryInput } from "./types";
import { focusAfter, focusBefore } from "./skyward.base.tabbing";

export interface EELOptions
{
    key?: JQuery<HTMLElement>;
    valueInput?: JQuery<HTMLElement>;
    beforeClose: () => void;
    beforeOpen: () => void;
    onSelectionListLoad: () => void;
    getSelectionListContainer: () => JQuery<HTMLElement>;
    getSelectionList: (includeSubsequentRecords?: boolean) => JQuery.Promise<string>;
    clearValues: () => void;
    setCurrentEnteredValue: (input: JQuery<HTMLElement>) => void;
    setCurrentHighlightedValue: () => void;
    alwaysExpose: JQueryInput[];
    selectionListPositioningOptions?: object;
    userInputs: UserInputOptions[];
}

export interface UserInputOptions
{
    input: JQuery<HTMLElement>;
    button: JQuery<HTMLElement>;
    customKeyDownHandler: (this: HTMLElement, event: KeyboardEvent) => void;
}

export interface EEL
{
    /**
     * Selects the current highlighted value (from the selection list).
     * Typically sets a flag and hides the modal/glass.
     */
    selectCurrentHighlightedValue(): void;

    /**
     * Shows the selection list for the provided input element.
     * Marks a user value as NOT selected, then shows the selection list in the correct container.
     * @param input - jQuery object representing the input field.
     */
    showSelectionList(input: JQuery<HTMLElement>): void;

    /**
     * Hides the selection list and overlay/glass.
     */
    hideSelectionList(): void;
}

const classNames =
{
    showEELContainer: "js-showEELContainer"
};

const defaultUserInput =
{
    input: null,
    button: null,
    customKeyDownHandler: function (/* event */) { /* Handle custom keydown events not already handled by this code */ }
};

export function setFocusToTextBoxOnClick(eel: JQuery<HTMLElement>)
{
    eel.find(".eELInputContainer").on('click', function ()
    {
        const textbox = $(this).find(".eELInputContainer__textBox");
        // Use native focus() to work around a bug in JQuery - likely 4950 or 4867
        // TODO (jquery 3.6.x) see if this is still needed
        if (textbox.length > 0)
        {
            textbox[0].focus();
        }
    });
}

function advanceFocusFromEel(input: JQuery<HTMLElement>, backward: boolean)
{
    if (backward)
    {
        focusBefore(input);
    }
    else
    {
        focusAfter(input);
    }
}

export function createEel(options?: Partial<EELOptions>)
{
    const defaultCodeEELOptions = getDefaultCodeEELOptions();
    const settings: EELOptions = $.extend({}, defaultCodeEELOptions, options);
    let modalLayer: ModalLayer;
    let eelValueListContainer: JQuery<HTMLElement>;
    let userIsChangingValue = false;
    let userSelectedValue = true;

    const getModalLayer = () => modalLayer;
    const setModalLayer = (newModalLayer: ModalLayer) => { modalLayer = newModalLayer };
    const getEelValueListContainer = () => eelValueListContainer;
    const setEelValueListContainer = (newEelValueListContainer: JQuery<HTMLElement>) => { eelValueListContainer = newEelValueListContainer };
    const getUserSelectedValue = () => userSelectedValue;

    const eelGlass = new EelGlass(getModalLayer, setModalLayer, getEelValueListContainer, setEelValueListContainer, settings, beforeClose);
    const eelValueList = new EelValueList(eelGlass, getModalLayer, getEelValueListContainer, settings, getUserSelectedValue);

    const eel: EEL =
    {
        selectCurrentHighlightedValue: function ()
        {
            userSelectedValue = true;
            settings.setCurrentHighlightedValue();
            eelGlass.hide();
        },
        showSelectionList: function (input)
        {
            userSelectedValue = false;
            eelValueList.show(input, input.closest(`.${classNames.showEELContainer}`), false);
        },
        hideSelectionList: function ()
        {
            eelValueList.hide();
            eelGlass.hide(false);
        }
    };

    $.each(settings.userInputs, function (_i, userInputOptions)
    {
        const userInputSettings: UserInputOptions = $.extend({}, defaultUserInput, userInputOptions);
        const input = userInputSettings.input;
        const button = userInputSettings.button;
        let lastValue = (input.val() as string).toLowerCase();

        input.on("input", function ()
        {
            const input = $(this);

            if (acceptInput(input, lastValue))
            {
                userIsChangingValue = true;
                userSelectedValue = false;
                eelValueList.hide();
            }
        });

        input.debounce("input", function (this: JQueryInput)
        {
            const input = $(this);

            if (acceptInput(input, lastValue))
            {
                eelValueList.show(input, input.closest(`.${classNames.showEELContainer}`));
                lastValue = (input.val() as string).toLowerCase();
            }
        });

        input.on("keydown", function (event)
        {
            const input = $(this);

            if (!acceptInput(input, null))
            {
                return;
            }

            // open modal using keyboard
            if (!eelGlass.isShowing() && !userIsChangingValue && (event.which === 39 || event.which === 40 || event.which === 13)) // if down, right or enter
            {
                userSelectedValue = false;
                eelValueList.show(input, input.closest(`.${classNames.showEELContainer}`), true);
                return false
            }
            // enter text, then tab out before the dialog shows (only possible on a slow connection)
            else if (!eelValueList.isShowing() && userIsChangingValue && (event.which === 9 || event.which === 13)) // if tab or enter
            {
                selectCurrentEnteredValue(input);
                if (event.which === 9) { advanceFocusFromEel(input, event.shiftKey); }
                return false;
            }
            // tab out after dialog shows
            else if (eelValueList.isShowing() && (event.which === 9 || event.which === 13))
            {
                eel.selectCurrentHighlightedValue();
                if (event.which === 9) { advanceFocusFromEel(input, event.shiftKey); }
                return false
            }
            else
            {
                return userInputSettings.customKeyDownHandler.apply(this, arguments as any);
            }
        });

        button.on("click", function ()
        {
            if (modalLayer == null || !modalLayer.onscreen)
            {
                const button = $(this);
                const input = $(userInputSettings.input);

                if (button.hasClass("ui-disabled") || !acceptInput(input))
                {
                    return;
                }

                userSelectedValue = false;
                eelValueList.show(input, input.closest(`.${classNames.showEELContainer}`), true);
            }
            else if (!mobileAccess)
            {
                modalLayer.close();
                return;
            }
        });
    });

    return eel as EEL;

    function beforeClose()
    {
        if (!userSelectedValue)
        {
            settings.clearValues();
        }

        eelValueList.hide();
        eelGlass.hide(true);
        userIsChangingValue = false;
        userSelectedValue = true;

        settings.beforeClose();
    }

    function selectCurrentEnteredValue(input: JQuery<HTMLElement>)
    {
        userSelectedValue = true;
        settings.setCurrentEnteredValue(input);
        eelGlass.hide();
    }

    function acceptInput(input: JQuery<HTMLElement>, _lastValue?: string | null)
    {
        if (input.is("[disabled],[readonly],.ui-disabled"))
        {
            return false;
        }

        return true;
    }
}

class EelGlass
{
    private glassIsShowing: boolean = false;
    private getModalLayer: () => ModalLayer;
    private setModalLayer: (newModalLayer: ModalLayer) => void;
    private getEelValueListContainer: () => JQuery<HTMLElement>;
    private setEelValueListContainer: (newEelValueListContainer: JQuery<HTMLElement>) => void;
    private settings: EELOptions;
    private beforeClose: () => void;

    constructor(
        getModalLayer: () => ModalLayer,
        setModalLayer: (newModalLayer: ModalLayer) => void,
        getEelValueListContainer: () => JQuery<HTMLElement>,
        setEelValueListContainer: (newEelValueListContainer: JQuery<HTMLElement>) => void,
        settings: EELOptions,
        beforeClose: () => void
    )
    {
        this.getModalLayer = getModalLayer;
        this.setModalLayer = setModalLayer;
        this.getEelValueListContainer = getEelValueListContainer;
        this.setEelValueListContainer = setEelValueListContainer;
        this.settings = settings;
        this.beforeClose = beforeClose;
    }

    isShowing(): boolean
    {
        return this.glassIsShowing;
    }

    hide(isClosing?: boolean): void
    {
        if (this.glassIsShowing && !isClosing)
        {
            this.getModalLayer().close();
        }

        this.glassIsShowing = false;
    }

    show(input: JQueryInput): void
    {
        if (!this.glassIsShowing)
        {
            this.setEelValueListContainer(this.settings.getSelectionListContainer());
            this.setModalLayer(newModalLayer(this.getEelValueListContainer(), this.beforeClose,
                {
                    expose: this.settings.alwaysExpose.concat([input]),
                    role: "listbox"
                }));
        }

        this.glassIsShowing = true;
    }
}

class EelValueList
{
    private eelValueListIsShowing: boolean = false;
    private eelGlass: EelGlass;
    private getModalLayer: () => ModalLayer;
    private getEelValueListContainer: () => JQuery<HTMLElement>;
    private settings: EELOptions;
    private userSelectedValue: () => boolean;

    constructor(
        eelGlass: EelGlass,
        getModalLayer: () => ModalLayer,
        getEelValueListContainer: () => JQuery<HTMLElement>,
        settings: EELOptions,
        userSelectedValue: () => boolean
    )
    {
        this.eelGlass = eelGlass;
        this.getModalLayer = getModalLayer;
        this.getEelValueListContainer = getEelValueListContainer;
        this.settings = settings;
        this.userSelectedValue = userSelectedValue;
    }

    isShowing(): boolean
    {
        return this.eelValueListIsShowing;
    }

    hide(): void
    {
        if (!this.eelGlass.isShowing())
        {
            return;
        }

        this.getEelValueListContainer().hide();
        this.getEelValueListContainer().html("");
        this.eelValueListIsShowing = false;
    }

    show(input: JQuery<HTMLElement>, containerToExpose: JQueryInput, includeSubsequentRecords?: boolean): void
    {
        if (this.userSelectedValue())
        {
            return;
        }
        this.settings.beforeOpen();

        this.eelGlass.show(containerToExpose);

        $.when(this.settings.getSelectionList(includeSubsequentRecords)).done((contents: string) =>
        {
            if (!this.eelGlass.isShowing() || this.userSelectedValue())
            {
                return;
            }

            // Use native focus() to work around a bug in JQuery - likely 4950 or 4867
            // TODO (jquery 3.6.x) see if this is still needed
            if (input.length > 0)
            {
                input[0].focus();
            }
            this.getEelValueListContainer().html(contents);
            this.getEelValueListContainer().show();
            this.eelValueListIsShowing = true;

            const positioningManager = createModalPositioningManager(this.getModalLayer(), containerToExpose, this.getModalLayer().contents, this.settings.selectionListPositioningOptions);
            this.getModalLayer().options.resizeCallback = positioningManager.positionEELContents;

            if (this.getModalLayer().options.resizeCallback && typeof this.getModalLayer().options.resizeCallback === "function")
            {
                // callback is unregistered in modallayer.js:1657
                registerLayoutResizeCallback(this.getModalLayer().options.resizeCallback!);
                this.getModalLayer().options.resizeCallback!();
            }

            this.settings.onSelectionListLoad();
        }).fail(() =>
        {
            this.eelGlass.hide();
        });
    }
}

function getDefaultCodeEELOptions()
{
    const defaults: EELOptions =
    {
        userInputs: [],
        getSelectionListContainer: function () { return $() },
        getSelectionList: function (_includeSubsequentRecords) { return $.Deferred(); },
        selectionListPositioningOptions: {},
        setCurrentEnteredValue: function (/* input */) { },
        setCurrentHighlightedValue: function () { },
        clearValues: function () { },
        alwaysExpose: [],
        beforeClose: function () { },
        beforeOpen: function () { },
        onSelectionListLoad: function () { }
    };

    return defaults;
}